import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Trophy, Crown, Star, Medal, Fire, Users, TrendUp } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BADGE_META = {
  'DIAMOND BACKER': { icon: Crown,  ring: 'from-[#facc15]/40 to-[#facc15]/8 border-[#facc15]/60',  text: 'text-[#facc15]', medal: '🥇' },
  'HYPE MASTER':    { icon: Star,   ring: 'from-[#c084fc]/40 to-[#c084fc]/8 border-[#c084fc]/60',  text: 'text-[#c084fc]', medal: '🥈' },
  'EARLY MOVER':    { icon: Medal,  ring: 'from-[#f472b6]/40 to-[#f472b6]/8 border-[#f472b6]/60',  text: 'text-[#f472b6]', medal: '🥉' },
};

const initials = (label) =>
  (label || '?').split(/\s+/).slice(0, 2).map((w) => w[0] || '').join('').toUpperCase();

const FanLeaderboard = ({ movieId, movieTitle }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!movieId) return;
    axios.get(`${BACKEND_URL}/api/movies/${movieId}/leaderboard?limit=10`)
      .then((r) => setData(r.data))
      .catch(() => setData(null));
  }, [movieId]);

  if (!data || (data.leaders || []).length === 0) {
    return (
      <section className="bg-white/[0.03] border border-dashed border-white/12 rounded-2xl p-6 text-center" data-testid="leaderboard-empty">
        <Trophy size={28} className="mx-auto text-white/70 mb-2" weight="bold" />
        <h3 className="font-extrabold text-sm mb-1">No top fans yet</h3>
        <p className="text-[11px] text-white/70 font-light">Be the first to grab a Backstage Pass or back this film — you'll claim the podium.</p>
      </section>
    );
  }

  const podium = data.leaders.slice(0, 3);
  const rest = data.leaders.slice(3);

  return (
    <section className="bg-gradient-to-br from-white/[0.06] via-white/[0.03] to-transparent border border-white/12 rounded-2xl p-5 sm:p-6" data-testid="fan-leaderboard">
      <div className="flex items-center gap-2 mb-1">
        <Trophy size={16} weight="fill" className="text-[#facc15]" />
        <h3 className="text-base font-extrabold tracking-tight">Top fans of <span className="text-[#facc15]">{movieTitle}</span></h3>
        <span className="ml-auto text-[10px] text-white/70 uppercase tracking-wider font-bold" data-testid="leaderboard-total-fans">
          <Users size={11} className="inline mr-1" />{data.total_fans} {data.total_fans === 1 ? 'fan' : 'fans'}
        </span>
      </div>
      <p className="text-xs text-white/70 font-light mb-5">
        Climb the podium with Backstage tiers, investments and friends you bring. Badges show on your Fraternity profile.
      </p>

      {/* Podium — top 3 in a tilted layout */}
      <div className="grid grid-cols-3 gap-2 sm:gap-3 mb-4" data-testid="leaderboard-podium">
        {podium.map((p) => {
          const meta = BADGE_META[p.badge] || BADGE_META['EARLY MOVER'];
          const Icon = meta.icon;
          // Visual lift: #1 floats up, #2 mid, #3 lowest
          const lift = p.rank === 1 ? '-translate-y-2 sm:-translate-y-3' : p.rank === 3 ? 'translate-y-0' : 'translate-y-0';
          return (
            <button
              key={p.user_id}
              data-testid={`leaderboard-rank-${p.rank}`}
              onClick={() => p.has_fraternity_profile && navigate(`/fraternity/u/${p.user_id}`)}
              className={`text-left bg-gradient-to-b ${meta.ring} to-transparent border rounded-xl p-3 sm:p-4 transition-transform ${lift} ${p.has_fraternity_profile ? 'hover:scale-[1.03] cursor-pointer' : 'cursor-default'}`}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-2xl">{meta.medal}</span>
                <span className="text-[10px] uppercase tracking-wider text-white/70 font-bold">#{p.rank}</span>
              </div>
              <div className="flex items-center gap-2 mb-2">
                {p.photo_url ? (
                  <img src={p.photo_url} alt={p.display_name} className="w-9 h-9 rounded-lg object-cover ring-1 ring-white/20 flex-shrink-0" />
                ) : (
                  <div className={`w-9 h-9 rounded-lg bg-gradient-to-br ${meta.ring.replace(/from-\[/g,'from-[').replace(/border-\[\S+\]/,'')} flex items-center justify-center font-extrabold text-[11px] text-black flex-shrink-0`}>
                    {initials(p.display_name)}
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-extrabold leading-tight truncate" data-testid={`leaderboard-name-${p.rank}`}>{p.display_name}</div>
                  {p.primary_role && <div className="text-[9px] text-white/70 uppercase tracking-wider truncate">{p.primary_role}</div>}
                </div>
              </div>
              <div className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full ${meta.text} bg-black/30 text-[9px] uppercase tracking-wider font-bold mb-2`}>
                <Icon size={9} weight="fill" /> {p.badge}
              </div>
              <div className="text-[10px] text-white/60 leading-relaxed font-light space-y-0.5">
                {p.backstage_tier && <div>🎟️ {p.backstage_tier} pass</div>}
                {p.invested_inr > 0 && <div>💰 ₹{p.invested_inr.toLocaleString('en-IN')}</div>}
                {p.referrals_invested > 0 && <div>🤝 {p.referrals_invested} ref{p.referrals_invested > 1 ? 's' : ''}</div>}
              </div>
            </button>
          );
        })}
      </div>

      {/* Rest of the top-10 as a tight list */}
      {rest.length > 0 && (
        <div className="border-t border-white/8 pt-3" data-testid="leaderboard-rest">
          <div className="text-[10px] uppercase tracking-wider text-white/70 font-bold mb-2">Runners-up</div>
          <div className="space-y-1.5">
            {rest.map((p) => (
              <button
                key={p.user_id}
                data-testid={`leaderboard-rank-${p.rank}`}
                onClick={() => p.has_fraternity_profile && navigate(`/fraternity/u/${p.user_id}`)}
                className={`w-full flex items-center gap-3 bg-black/30 border border-white/8 rounded-lg px-3 py-2 ${p.has_fraternity_profile ? 'hover:border-white/20 cursor-pointer' : 'cursor-default'}`}
              >
                <span className="text-[10px] uppercase tracking-wider text-white/70 font-bold w-6 text-center">#{p.rank}</span>
                {p.photo_url ? (
                  <img src={p.photo_url} alt="" className="w-6 h-6 rounded object-cover flex-shrink-0" />
                ) : (
                  <div className="w-6 h-6 rounded bg-gradient-to-br from-white/20 to-white/5 flex items-center justify-center text-[9px] font-extrabold flex-shrink-0">{initials(p.display_name)}</div>
                )}
                <span className="text-xs font-bold flex-1 min-w-0 truncate text-left">{p.display_name}</span>
                <span className="text-[10px] text-white/70 tabular-nums">{p.score.toFixed(0)} pts</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* CTA to climb */}
      <div className="mt-4 flex items-center gap-2 bg-gradient-to-r from-[#facc15]/10 to-transparent border border-[#facc15]/25 rounded-lg px-3 py-2">
        <Fire size={14} weight="fill" className="text-[#facc15]" />
        <span className="text-[11px] text-white/80 font-light flex-1">
          Climb the podium — grab a Backstage Pass, invite friends with your <strong className="text-[#facc15]">referral code</strong>, or back this film.
        </span>
        <TrendUp size={14} weight="bold" className="text-[#facc15]" />
      </div>
    </section>
  );
};

export default FanLeaderboard;
